import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faGift, faCreditCard, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './ServiceWallet.css';

export interface CreditPackOption {
  id: number;
  name: string;
  credits: number;
  bonusCredits?: number;
  totalCredits?: number;
  price: number;
  currency: string;
  description?: string;
  features?: string[];
  featured?: boolean;
  badge?: string;
}

interface CreditPackGridProps {
  packs: CreditPackOption[];
  onBuy: (id: number) => void;
  processingId?: number | null;
}

const currencySymbol = (currency: string) => (currency.toUpperCase() === 'INR' ? '\u20B9' : '$');

const CreditPackGrid: React.FC<CreditPackGridProps> = ({ packs, onBuy, processingId = null }) => {
  const { t } = useTranslation('services');

  return (
    <div className="sw-packs">
      {packs.map((pack) => {
        const bonus = pack.bonusCredits ?? 0;
        const total = pack.totalCredits ?? pack.credits + bonus;
        const featured = pack.featured ?? total >= 10;
        const badge = pack.badge ?? (featured ? t('wallet.bestValue') : undefined);
        const processing = processingId === pack.id;

        return (
          <div key={pack.id} className={`sw-pack${featured ? ' sw-pack--featured' : ''}`}>
            {badge && <div className="sw-pack-badge">{badge}</div>}
            <h3 className="sw-pack-name">{pack.name}</h3>
            <div className="sw-pack-price">
              <span className="sw-pack-currency">{currencySymbol(pack.currency)}</span>
              <span className="sw-pack-amount">{pack.price.toFixed(0)}</span>
            </div>
            <div className="sw-pack-meta">
              <span className="sw-pack-credits">
                {t('wallet.creditsCount', { credits: total })}
              </span>
              {bonus > 0 && (
                <span className="sw-pack-bonus">
                  <FontAwesomeIcon icon={faGift} />
                  {t('wallet.bonusCount', { bonus })}
                </span>
              )}
              {total > 1 && (
                <span>
                  {currencySymbol(pack.currency)}
                  {(pack.price / total).toFixed(0)} {t('wallet.perCredit')}
                </span>
              )}
            </div>
            {pack.description && <p className="sw-pack-description">{pack.description}</p>}
            {pack.features && pack.features.length > 0 && (
              <ul className="sw-pack-features">
                {pack.features.map((feature, index) => (
                  <li key={index}>
                    <FontAwesomeIcon icon={faCheck} /> {feature}
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              className="sw-pack-buy"
              onClick={() => onBuy(pack.id)}
              disabled={processingId !== null}
            >
              <FontAwesomeIcon icon={processing ? faSpinner : faCreditCard} spin={processing} />
              {processing ? t('wallet.processing') : t('wallet.buyNow')}
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default CreditPackGrid;
